import React, { useContext, useState, useEffect, useRef } from "react";
import { Button, Alert, Text, Box } from "@mantine/core";
import {
  createCaptureSdk,
  createCaptureUiWithInstance,
} from "@microblink/capture";
import "@microblink/capture/style.css";
import { uploadImageToS3 } from "../../../../helpers";
import { DataContext } from "../../../../context/DataContext";
import { IconExclamationCircle } from "@tabler/icons-react";
import { isMobile, isFirefox } from "react-device-detect";
import FirefoxCameraWarning from "../../../../components/FirefoxCameraWarning";

export default function Microblink({ onComplete, onStart, onUpload, qrScanned }) {
  const { settings, text, data } = useContext(DataContext);
  const [sdk, setSdk] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFirefoxPrompt, setShowFirefoxPrompt] = useState(
    isMobile && isFirefox
  );
  const [showCaptureOverlay, setShowCaptureOverlay] = useState(false);
  const captureStateUnsubscribeRef = useRef(null);
  const language = (
    data?.language ||
    settings?.language ||
    settings?.text?.language ||
    text?.language ||
    ""
  ).toLowerCase();
  const isJapaneseLanguage = language === "ja";

  async function processAndUpload(data, width, height, suffix) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.putImageData(new ImageData(data, width, height), 0, 0);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 1.0)
    );
    await uploadImageToS3(`${settings.request_id}_dl_${suffix}.jpeg`, blob, settings);
  }

  async function sendResultToApi(requestId, documentGroup, cameraInfo = null) {
    try {
      const payload = {
        request_id: requestId,
        document_group: documentGroup,
      };

      // Add camera info if provided (send raw data without filtering)
      if (cameraInfo) {
        payload.camera_info = {
          name: cameraInfo.name,
          deviceInfo: cameraInfo.deviceInfo,
          settings: cameraInfo.settings,
        };
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/microblink/stats`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const result = await response.json();
      if (result.result !== "success") {
        console.error("Failed to send result to API:", result);
      }
    } catch (error) {
      await sendResultToApi(settings.request_id, error.message);
      console.error("Error while sending result to API:", error);
    }
  }

  const captureComponentRef = useRef(null);
  // guard against multiple simultaneous capture sessions
  const isCapturingRef = useRef(false);

  useEffect(() => {
    // Preload the SDK
    async function preloadSdk() {
      if (showFirefoxPrompt) return;
      setIsLoading(true);
      try {
        const mbKey = process.env.REACT_APP_MB_KEY;
        console.log('Microblink SDK key (first 8 chars):', mbKey?.substring(0, 8));

        const loadedSdk = await createCaptureSdk({
          licenseKey: mbKey,
          resourceUrl: `${window.location.protocol}//${window.location.hostname}:${window.location.port}`,
        });

        // Disable blur detection for all desktop devices and specified sites
        if(settings.site_name === 'GAVS-PROD-TEST' || 
           settings.site_name === "GAVS-PROD-RO" || 
           !isMobile){
          await loadedSdk.updateAnalyzerSettings({
            blurPolicy: "disabled", // Disables blur detection
            glarePolicy: "disabled" // Also disable glare detection
          });
        }
        
        // For Veratad sites, optimize for quality with enhanced settings
        if(settings.site_name && settings.site_name.toLowerCase().includes('veratad')){
          await loadedSdk.updateAnalyzerSettings({
            captureStrategy: "optimize-for-quality",
            adjustMinimumDocumentDpi: true,
            minimumDocumentDpi: 400,
            keepDpiOnTransformedDocumentImage: true,
            lightingThresholds: {
              // Allow darker lighting; frames below this are too dark (0-1 scale)
              tooDarkThreshold: 0.1,
            }
          });
        }

        // For KID environments, allow darker ambient lighting before capture
        if (
          settings.site_name &&
          ["kid-qa", "kid-pre-prod"].includes(
            settings.site_name.toLowerCase()
          )
        ) {
          await loadedSdk.updateAnalyzerSettings({
            lightingThresholds: {
              // Allow darker ambient lighting; frames below this are considered too dark
              tooDarkThreshold: 0.1,
            },
          });
        }

        setSdk(loadedSdk);
      } catch (error) {
        await sendResultToApi(settings.request_id, error.message);
        setErrorMessage("Failed to initialize the SDK: " + error.message);
      }
      setIsLoading(false);
    }

    preloadSdk();

    // Cleanup on component unmount
    return () => {
      captureStateUnsubscribeRef.current?.();
      captureComponentRef.current?.dismount();
      setShowCaptureOverlay(false);
    };
  }, [showFirefoxPrompt]);

  async function startCapture() {
    // prevent starting the capture process if one is already running
    if (isCapturingRef.current) {
      return;
    }
    isCapturingRef.current = true;
    setIsLoading(true);
    onStart();
    if (!sdk) {
      await sendResultToApi(
        settings.request_id,
        "SDK not initialized, please reload the page."
      );
      setErrorMessage("SDK not initialized, please reload the page.");
      setIsLoading(false);
      isCapturingRef.current = false;
      return;
    }
    if (isJapaneseLanguage) {
      setShowCaptureOverlay(true);
    }
    await sendResultToApi(settings.request_id, "SDK loaded");

    try {
      const component = await createCaptureUiWithInstance(sdk, {
        target: undefined,
        showTutorial: false,
        showErrorDialog: false,
        destroyInstanceOnDismount: true,
        localization: {
          ...text.captureMessages,
          ...text.tutorialMessages,
          ...text.cameraMessages,
          ...text.sharedMessages,
        },
      });

      const originalDismount = component.dismount.bind(component);
      component.dismount = () => {
        captureStateUnsubscribeRef.current?.();
        setShowCaptureOverlay(false);
        setIsLoading(false);
        isCapturingRef.current = false;
        return originalDismount();
      };

      captureStateUnsubscribeRef.current?.();
      captureStateUnsubscribeRef.current = component.captureSdk?.subscribe(
        (s) => s.isPlaying || s.isCapturing,
        (isActive) => {
          if (isActive && isJapaneseLanguage) {
            setShowCaptureOverlay(true);
          } else if (!isActive) {
            setShowCaptureOverlay(false);
            // Ensure the intro button becomes clickable again when SDK is closed
            setIsLoading(false);
            isCapturingRef.current = false;
          }
        }
      );

      component.captureSdk?.subscribe(
        (s) => s.errorState,
        async (x) => {
          if (x?.message !== undefined) {
            setErrorMessage(x?.message);
            console.error("Error from the SDK: ", x?.message);

            try {
              await sendResultToApi(settings.request_id, x?.message);
            } catch (error) {
              console.error("Error sending result to API:", error);
            }

            component.dismount();
            setIsLoading(false);
            isCapturingRef.current = false;
            setShowCaptureOverlay(false);
          }
        }
      );

      component.captureSdk?.setCallbacks({
        onCaptureResult: async (result) => {
          // Get camera info from SDK state
          const selectedCamera = component.captureSdk?.getState().selectedCamera;

          await sendResultToApi(settings.request_id, result.documentGroup, selectedCamera);

          if (
            settings.journey.settings?.checkMbDocumentGroup &&
            result.documentGroup === "unknown"
          ) {
            component.dismount();
            setErrorMessage("The document type is unknown. Please try again.");
            setIsLoading(false);
            isCapturingRef.current = false;
            setShowCaptureOverlay(false);
            return;
          }

          component.dismount();
          onUpload();
          await processAndUpload(
            result.firstCapture.imageResult.data,
            result.firstCapture.imageResult.width,
            result.firstCapture.imageResult.height,
            "front"
          );
          if (result.secondCapture?.imageResult) {
            await processAndUpload(
              result.secondCapture.imageResult.data,
              result.secondCapture.imageResult.width,
              result.secondCapture.imageResult.height,
              "back"
            );
          }
          onComplete();
          setIsLoading(false);
          setShowCaptureOverlay(false);
          isCapturingRef.current = false;
        },
      });

      captureComponentRef.current = component;
    } catch (error) {
      await sendResultToApi(settings.request_id, error.message);
      setErrorMessage("Failed to start the capture process: " + error.message);
      setIsLoading(false);
      setShowCaptureOverlay(false);
      isCapturingRef.current = false;
    }
  }

  if (showFirefoxPrompt) {
    return (
      <FirefoxCameraWarning onContinue={() => setShowFirefoxPrompt(false)} />
    );
  }

  return (
    <>
      {errorMessage && (
        <Alert
          icon={<IconExclamationCircle />}
          color="red"
          title={errorMessage}
        >
          {errorMessage !==
            "The document type is unknown. Please try again." && (
            <Text>{text.dcams.errors.permissions}</Text>
          )}
        </Alert>
      )}
      <Button
        size="lg"
        fullWidth
        onClick={startCapture}
        disabled={!sdk || isLoading || qrScanned}
        loading={!sdk || isLoading}
        loaderPosition="left"
      >
        {text.dcams.button}
      </Button>
      {showCaptureOverlay && isJapaneseLanguage && (
        <Box
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 16,
            display: "flex",
            justifyContent: "center",
            zIndex: 9999,
            pointerEvents: "none",
          }}
        >
          <Box
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.85)",
              color: "white",
              padding: "12px 16px",
              borderRadius: 8,
              maxWidth: 520,
              boxShadow: "0 6px 18px rgba(0, 0, 0, 0.35)",
            }}
          >
            <Text align="center" fz="sm">
              マイナンバーカードの場合は、
              <Text span fw={700}>個人番号およびQR</Text>
              が隠されていることを確認してください。
            </Text>
          </Box>
        </Box>
      )}
    </>
  );
}
